import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./HomeVision.css";
import { doors, colors } from "../door.js";

function HomeVision() {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [doorBoundingBox, setDoorBoundingBox] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [compositedImages, setCompositedImages] = useState([]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);

      // Create a preview of the image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
        detectDoor(file);
      };
      reader.readAsDataURL(file);
      setDoorBoundingBox(null);
    }
  };

  // Function to predict image and get bounding boxes
  async function predictImage(
    imageFile,
    samType,
    boxThreshold,
    textThreshold,
    textPrompt
  ) {
    const url = `https://sam2-detection-631509657635.us-central1.run.app/predict`;

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("sam_type", samType);
    formData.append("box_threshold", boxThreshold.toString());
    formData.append("text_threshold", textThreshold.toString());
    formData.append("text_prompt", textPrompt);

    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error making prediction:", error);
      throw error;
    }
  }

  // Function to detect door - separated for cleaner code
  const detectDoor = async (imageFile) => {
    if (!imageFile) return;

    setIsLoading(true);

    try {
      const result = await predictImage(
        imageFile,
        "sam2.1_hiera_small", // Using the highest quality model
        0.3, // Box threshold
        0.25, // Text threshold
        "doors" // Text prompt to detect doors
      );

      // Get the first bounding box from the results
      if (result.boxes && result.boxes.length > 0) {
        const firstBox = result.boxes[0];

        console.log(firstBox);
        setDoorBoundingBox(firstBox);
      } else {
        // No door detected
        console.log("No door detected in the image");
        alert("No door detected in the image. Please try another image.");
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error detecting door:", error);
      alert("Error detecting door. Please try again.");
      setIsLoading(false);
    }
  };

  const handleInspireMe = async () => {
    if (!doorBoundingBox) return;

    setIsLoading(true);
    // Reset previous results when starting a new request
    setCompositedImages([]);

    try {
      // Get the host URL (assumes we're running on the same host as the API)
      const host = window.location.origin;

      // Get the first 10 doors from the doors array
      const firstTenDoors = doors.slice(10, 100);

      // Create 6 combinations of doors and colors
      const combinations = [];
      for (let i = 0; i < 6; i++) {
        // Select a door from the first 10 doors
        const doorIndex = Math.floor(Math.random() * firstTenDoors.length);
        const door = firstTenDoors[doorIndex];

        // Select a random color from all available colors
        const colorIndex = Math.floor(Math.random() * colors.length);
        const color = colors[colorIndex];

        // Add the combination to our array
        combinations.push({
          door: door.name,
          color: color.name,
        });
      }

      // Generate doorimages URLs
      const doorImages = combinations.map(
        (combo) =>
          `${host}/api/process/${combo.door}/${combo.color.toLowerCase()}`
      );

      // Create FormData to properly handle file upload
      const formData = new FormData();
      formData.append("houseImage", selectedImage);
      formData.append("boxes", JSON.stringify([doorBoundingBox]));
      formData.append("doorImages", JSON.stringify(doorImages));

      // Call the composite API
      const response = await fetch(`${host}/api/composite`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Process the results
      const result = await response.json();

      console.log("Inspiration results:", result);

      // Store the composited images
      if (result.composited_images && result.composited_images.length > 0) {
        setCompositedImages(result.composited_images);
      } else {
        alert("No composited images were generated. Please try again.");
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error getting inspiration:", error);
      alert("Error getting door inspirations. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="home-vision">
      <div className="header">
        <button className="back-button" onClick={() => navigate("/")}>
          ← Back to Home
        </button>
        <h1>Home Vision</h1>
      </div>

      <div className="upload-section">
        <h2>Upload Your Door Image</h2>
        <p>
          Upload a photo of your home's door to get inspiration for new door
          designs.
        </p>

        <div className="upload-container">
          <label className="upload-button">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />
            {isLoading ? "Processing..." : "Choose Image"}
          </label>

          {selectedImage && (
            <div className="file-name">{selectedImage.name}</div>
          )}
        </div>
      </div>

      <div className="preview-section">
        {previewImage ? (
          <div className="image-preview-container">
            <div className="image-preview">
              <img
                src={previewImage}
                alt="Uploaded door"
                className="preview-img"
              />
              {doorBoundingBox && (
                <div
                  className="bounding-box"
                  style={{
                    left: `${doorBoundingBox.x}px`,
                    top: `${doorBoundingBox.y}px`,
                    width: `${doorBoundingBox.width}px`,
                    height: `${doorBoundingBox.height}px`,
                  }}
                ></div>
              )}
            </div>

            <div className="action-buttons">
              <button
                className="inspire-button"
                onClick={handleInspireMe}
                disabled={!doorBoundingBox || isLoading}
              >
                {isLoading ? "Loading..." : "Inspire Me"}
              </button>
            </div>
          </div>
        ) : (
          <div className="placeholder-container">
            <div className="upload-placeholder">
              <p>No image selected</p>
              <p>Upload an image to get started</p>
            </div>
          </div>
        )}
      </div>

      {/* Display composited images */}
      {compositedImages.length > 0 && (
        <div className="composited-images-section">
          <h2>Door Inspirations</h2>
          <div className="composited-images-grid">
            {compositedImages.map((data, index) => (
              <div key={index} className="composited-image-card">
                <img
                  src={`data:image/jpeg;base64,${data.image_base64}`}
                  alt={`Door Inspiration ${index + 1}`}
                  className="composited-img"
                />
                <div className="image-caption">Option {index + 1}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeVision;
